/**
 * useTasksBoard · hook del Kanban (PR-T3 del rediseño v2.0).
 *
 * Responsabilidades:
 *  - Cargar tareas filtradas (scope / area / priority).
 *  - Agruparlas por status (kanban) o por otra dimensión configurable.
 *  - Exponer una mutation `moveTask` con optimistic update: la card aparece
 *    en la nueva columna inmediatamente y, si la mutation falla, se hace
 *    rollback de la cache. Si la mutation triunfa, igual invalidamos para
 *    refrescar datos derivados (campanita, my-due, etc.).
 *
 * Diff vs el viejo useTasks:
 *  - useTasks devuelve un array flat. Esto da un Map<groupKey, TasksRow[]>.
 *  - Hace optimistic move (no había antes — la UI esperaba al backend).
 *  - El cliente PUEDE seguir leyendo el array flat vía `data` por si se
 *    necesita en otras vistas (lista, mine, etc.).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import type {
  TaskPriority,
  TaskScope,
  TaskStatus,
  TasksRow,
} from '@/types/database.types';

import * as tasksApi from '../api/tasks';
import { KANBAN_COLUMNS } from '../schemas/task.schema';
import { TASK_KEYS } from './keys';

export type BoardGroupBy = 'status' | 'priority' | 'area' | 'assignee';

export interface UseTasksBoardFilters {
  scope?: TaskScope;
  /** Filtra por área. `null` no filtra (todas), un id filtra a esa área. */
  areaId?: string | null;
  /** Filtra por prioridad. `null` no filtra. */
  priority?: TaskPriority | null;
  /** Filtra por estado. `null` no filtra. */
  status?: TaskStatus | null;
  /** Eje de agrupación. Default 'status' (kanban clásico). */
  groupBy?: BoardGroupBy;
  /** Filtro por proyecto (workspace de proyecto · Anexo B). */
  projectId?: string | null;
  /**
   * Si true, incluye subtareas en el board (mezcladas con sus padres).
   * Default false → las subtareas se ven sólo dentro del drawer del padre.
   */
  includeSubtasks?: boolean;
}

interface MoveTaskInput {
  taskId: string;
  newStatus: TaskStatus;
}

/**
 * Helper · re-agrupa una lista flat por el eje dado.
 * Para 'status' usa KANBAN_COLUMNS como orden y elimina 'archived'.
 */
function groupTasks(
  tasks: TasksRow[],
  groupBy: BoardGroupBy,
): Map<string, TasksRow[]> {
  const map = new Map<string, TasksRow[]>();

  if (groupBy === 'status') {
    // Pre-llenar columnas en orden para que aparezcan aunque estén vacías.
    for (const col of KANBAN_COLUMNS) map.set(col, []);
    for (const t of tasks) {
      if (t.status === 'archived') continue;
      map.get(t.status)?.push(t);
    }
    return map;
  }

  // Otros agrupamientos (lista densa, mine, etc.) — orden de columnas
  // determinado por el orden en que aparecen las keys.
  for (const t of tasks) {
    let key: string;
    if (groupBy === 'priority') key = t.priority;
    else if (groupBy === 'area') key = t.area_id ?? '__personal__';
    else key = t.created_by ?? '__unassigned__';

    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(t);
  }
  return map;
}

export function useTasksBoard(filters: UseTasksBoardFilters = {}) {
  const qc = useQueryClient();
  const {
    scope,
    areaId,
    priority,
    status,
    groupBy = 'status',
    projectId,
    includeSubtasks = false,
  } = filters;

  // Query base: la lista cruda. RLS sigue siendo la verdad sobre visibilidad.
  // Aplicamos `priority` en cliente (no es columna indexada y la fact-card
  // es chica · evita un nuevo round-trip cuando cambia el chip).
  const apiFilters: tasksApi.ListTasksFilters = useMemo(
    () => ({
      scope,
      areaId: areaId ?? undefined,
      projectId: projectId ?? undefined,
      includeSubtasks,
    }),
    [scope, areaId, projectId, includeSubtasks],
  );

  const tasksQuery = useQuery({
    queryKey: TASK_KEYS.list(apiFilters),
    queryFn: () => tasksApi.listTasks(apiFilters),
    staleTime: 30_000,
  });

  const filteredTasks = useMemo(() => {
    let list = tasksQuery.data ?? [];
    if (priority) list = list.filter((t) => t.priority === priority);
    if (status) list = list.filter((t) => t.status === status);
    return list;
  }, [tasksQuery.data, priority, status]);

  const grouped = useMemo(
    () => groupTasks(filteredTasks, groupBy),
    [filteredTasks, groupBy],
  );

  // ============================================================
  // Optimistic DnD move
  // ============================================================
  const moveMutation = useMutation({
    mutationFn: ({ taskId, newStatus }: MoveTaskInput) =>
      tasksApi.updateTask(taskId, { status: newStatus }),

    onMutate: async ({ taskId, newStatus }) => {
      // Pause re-fetches mientras hacemos el optimistic update
      await qc.cancelQueries({ queryKey: TASK_KEYS.list(apiFilters) });

      // Snapshot para rollback
      const previous = qc.getQueryData<TasksRow[]>(TASK_KEYS.list(apiFilters));
      if (!previous) return { previous };

      // Aplicar el cambio en memoria
      const next = previous.map((t) =>
        t.id === taskId ? { ...t, status: newStatus } : t,
      );
      qc.setQueryData(TASK_KEYS.list(apiFilters), next);

      return { previous };
    },

    onError: (error, _vars, ctx) => {
      // Rollback
      if (ctx?.previous) {
        qc.setQueryData(TASK_KEYS.list(apiFilters), ctx.previous);
      }
      toast.error('No se pudo mover la tarea', {
        description: (error as Error).message,
      });
    },

    onSettled: (task) => {
      // Invalidaciones generales — la campanita y el detalle se enteran
      qc.invalidateQueries({ queryKey: ['core', 'tasks'] });
      qc.invalidateQueries({ queryKey: TASK_KEYS.myDue });
      if (task) {
        qc.invalidateQueries({ queryKey: TASK_KEYS.activity(task.id) });
      }
    },
  });

  return {
    /** Lista flat ya filtrada (útil para vistas Lista / Calendar / Mine). */
    tasks: filteredTasks,
    /** Map<groupKey, TasksRow[]> según el eje configurado. */
    grouped,
    isLoading: tasksQuery.isLoading,
    isError: tasksQuery.isError,
    error: tasksQuery.error,
    /** Mover una tarea entre columnas (optimistic). */
    moveTask: moveMutation.mutateAsync,
    isMoving: moveMutation.isPending,
  };
}
